import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ExternalLink, Trash2, Power } from "lucide-react";

export default function AdminQuestions() {
  const [items, setItems] = useState([]);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("search") || "");

  const load = async () => {
    const { data } = await api.get("/questions", { params: { q, include_drafts: true, limit: 200 } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onDelete = async (item) => {
    const msg = (item.attempts_count || 0) > 0
      ? `"${item.ref}" öğrenciler tarafından çözülmüş. Sistem silmek yerine pasifleştirecek. Devam edilsin mi?`
      : `"${item.ref}" henüz hiçbir öğrenci tarafından çözülmemiş. Kalıcı olarak silmek istediğinize emin misiniz?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/questions/${item.question_id}`);
      if (data.deleted) toast.success(data.message || "Soru silindi.");
      else toast.message(data.message || "Soru pasifleştirildi.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const onToggleActive = async (item) => {
    try {
      await api.post(`/questions/${item.question_id}/toggle-active`);
      toast.success(item.is_active === false ? "Soru tekrar aktifleştirildi." : "Soru pasifleştirildi.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl tracking-tight">Sorular</h1>
        <Link to="/admin/sorular/yeni" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#EA580C] hover:bg-[#F97316]">
          <Plus size={16}/> Yeni Soru
        </Link>
      </div>
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: MAT-1234 veya metin"
          data-testid="admin-questions-search"
          className="flex-1 px-3 py-2 rounded-md bg-[#171717] border border-[#262626] outline-none focus:border-[#EA580C]" />
        <button onClick={load} className="px-4 py-2 rounded-md bg-[#171717] border border-[#262626] hover:border-[#EA580C]">Ara</button>
      </div>
      <div className="border border-[#262626] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#171717] text-slate-400 text-xs uppercase">
            <tr>
              <th className="text-left p-3">Ref</th>
              <th className="text-left p-3">Metin</th>
              <th className="text-left p-3">Durum</th>
              <th className="text-left p-3">Zorluk</th>
              <th className="text-left p-3">Denenen</th>
              <th className="text-left p-3">Başarı</th>
              <th className="p-3 w-56"/>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const acc = r.attempts_count ? Math.round((r.correct_count / r.attempts_count) * 100) : 0;
              const active = r.is_active !== false;
              return (
                <tr key={r.question_id}
                    data-testid={`admin-question-row-${r.question_id}`}
                    className={`border-t border-[#262626] hover:bg-[#171717] ${active ? "" : "opacity-70"}`}>
                  <td className="p-3 font-mono">{r.ref}</td>
                  <td className="p-3 max-w-md truncate">{r.text}</td>
                  <td className="p-3">
                    <span data-testid={`admin-question-status-${r.question_id}`}
                      className={`inline-block px-2 py-0.5 rounded-full text-xs ${active ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>
                      {active ? "Aktif" : "Pasif"}
                    </span>
                  </td>
                  <td className="p-3">
                    {r.difficulty_status === "computed" && r.difficulty_score != null ? (
                      <span data-testid={`admin-question-difficulty-${r.question_id}`}
                        className="inline-block px-2 py-0.5 rounded-full text-xs bg-[#EA580C]/15 text-[#FDBA74] font-mono">
                        {Number(r.difficulty_score).toFixed(2)}
                      </span>
                    ) : (
                      <span data-testid={`admin-question-difficulty-${r.question_id}`}
                        className="text-xs text-slate-500 italic">
                        Zorluk belirleniyor
                      </span>
                    )}
                  </td>
                  <td className="p-3">{r.attempts_count || 0}</td>
                  <td className="p-3">{acc}%</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <Link to={`/admin/sorular/${r.question_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs mr-1">
                      Düzenle <ExternalLink size={12}/>
                    </Link>
                    {active ? (
                      <button onClick={() => onToggleActive(r)}
                        data-testid={`admin-question-deactivate-${r.question_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs mr-1">
                        <Power size={12}/> Pasifleştir
                      </button>
                    ) : (
                      <button onClick={() => onToggleActive(r)}
                        data-testid={`admin-question-activate-${r.question_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 text-xs mr-1">
                        <Power size={12}/> Tekrar Aktifleştir
                      </button>
                    )}
                    <button onClick={() => onDelete(r)}
                      data-testid={`admin-question-delete-${r.question_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs">
                      <Trash2 size={12}/> Sil
                    </button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan="7" className="p-8 text-center text-slate-500">Henüz soru yok.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
